/**
 * Textbausteine der Fliesstextkapitel.
 *
 * Katalog im Repo, Auswahl und Wortlaut am Datensatz — Begruendung wie in
 * `ebvKlassen.ts`: laege der Text als Stammtabelle in der Datenbank und wuerde
 * fortgeschrieben, aenderte sich rueckwirkend, was in einem freigegebenen Plan
 * steht.
 *
 * ═══ WAS EIN BAUSTEIN IST UND WAS NICHT ═══════════════════════════════════
 * Ein Baustein traegt das, was auf JEDER Baustelle dieser Art gilt: die
 * Rangfolge der Schutzmassnahmen, die Hygieneregeln, die Anzeigepflicht, das
 * Verhalten bei Unregelmaessigkeiten. Was projektbezogen ist — Befunde,
 * Bereiche, Klassen, Namen, Fristen —, steht NICHT hier, sondern in den
 * Tabellen.
 *
 * Die Trennung ist keine Ordnungsfrage: ein Baustein, der eine projektbezogene
 * Aussage enthaelt, wird beim naechsten Plan mitkopiert und behauptet dort
 * etwas, das niemand geprueft hat.
 *
 * `pflicht` heisst: ohne diesen Baustein fehlt dem Kapitel seine Aussage. Er
 * ist vorbelegt und laesst sich abwaehlen — aber die Vollstaendigkeitspruefung
 * fragt dann nach.
 */

export const BAUSTEIN_KATALOG_VERSION = '2026-08-22';

export interface Baustein {
    key: string;
    kapitel_key: string;
    titel: string;
    text: string;
    pflicht: boolean;
    /** Nur fuer diese Regelwerksschiene anbieten; leer = fuer beide. */
    regelwerk?: 'dguv101_004' | 'trgs524';
    veraltet?: boolean;
}

export interface Kapitel {
    key: string;
    nr: string;
    titel: string;
    hinweis?: string;
}

export const BAUSTEIN_KAPITEL: Kapitel[] = [
    { key: 'notwendigkeit', nr: '1.2', titel: 'Notwendigkeit des Arbeitsschutzes' },
    {
        key: 'koordinator', nr: '1.3', titel: 'Koordinator nach DGUV Regel 101-004',
        hinweis: 'Der Koordinator nach DGUV R 101-004 ist nicht automatisch der SiGeKo.',
    },
    { key: 'personenkreis', nr: '1.5', titel: 'Betroffener Personenkreis und Alleinarbeit' },
    { key: 'aufsicht', nr: '1.6', titel: 'Leitung und Aufsicht' },
    { key: 'gueltigkeit', nr: '1.7', titel: 'Gültigkeitsdauer' },
    { key: 'anzeige', nr: '1.8', titel: 'Anzeigepflicht des Auftragnehmers' },
    { key: 'plausibilitaet', nr: '3.3', titel: 'Plausibilisierung der Probennahmestrategie' },
    { key: 'wirkungspfade', nr: '6', titel: 'Wirkungspfade und Gefährdungsabschätzung' },
    { key: 'technisch', nr: '7.1', titel: 'Technische Schutzmaßnahmen' },
    { key: 'organisatorisch', nr: '7.2', titel: 'Organisatorische Schutzmaßnahmen' },
    { key: 'hygiene', nr: '7.2.3', titel: 'Verhaltensregeln und Baustellenhygiene' },
    { key: 'arbeitsmedizin', nr: '7.2.4', titel: 'Arbeitsmedizinische Vorsorge und Biomonitoring' },
    { key: 'notfall', nr: '7.2.6', titel: 'Notfallorganisation' },
    { key: 'beschaeftigung', nr: '7.2.9', titel: 'Beschäftigungsbeschränkungen' },
    { key: 'unterweisung', nr: '7.2.10', titel: 'Unterweisung' },
    { key: 'explosionsschutz', nr: '7.2.13', titel: 'Explosionsschutz und Heißarbeiten' },
    { key: 'erste_hilfe', nr: '10', titel: 'Erste Hilfe und Rettung' },
    { key: 'brandschutz', nr: '12', titel: 'Brandschutz' },
    { key: 'hautschutz', nr: '13', titel: 'Hautschutz' },
];

export const BAUSTEINE: Baustein[] = [
    // ── 1.2 Notwendigkeit ──────────────────────────────────────────────────
    {
        key: 'absperrung', kapitel_key: 'notwendigkeit', pflicht: true,
        titel: 'Absperrung und Kennzeichnung',
        text: 'Die Arbeitsbereiche sind von den öffentlich begehbaren Bereichen sichtbar '
            + 'abzutrennen und zu kennzeichnen („Betreten verboten“). Der Zutritt ist nur '
            + 'nach Einweisung und mit der für den Bereich festgelegten Schutzausrüstung '
            + 'zulässig.',
    },
    {
        key: 'parallelbetrieb', kapitel_key: 'notwendigkeit', pflicht: false,
        titel: 'Paralleler Betrieb mehrerer Bereiche',
        text: 'Die Arbeiten in den einzelnen Bereichen können nacheinander oder parallel '
            + 'erfolgen. Bei paralleler Bearbeitung sind die Schwarzbereiche getrennt zu '
            + 'halten; die zeitliche Abfolge stimmt der Koordinator mit der Bauleitung ab.',
    },

    // ── 1.3 Koordinator ────────────────────────────────────────────────────
    {
        key: 'aufgaben', kapitel_key: 'koordinator', pflicht: true,
        titel: 'Aufgaben des Koordinators (Abschnitt 5.2)',
        regelwerk: 'dguv101_004',
        text: 'Zu den Aufgaben des Koordinators zählen: Aufstellen des baustellenbezogenen '
            + 'Arbeits- und Sicherheitsplans; Einweisen aller Beteiligten in die Gefährdungen '
            + 'und die erforderlichen Schutzmaßnahmen; Überwachen der in den '
            + 'Betriebsanweisungen festgelegten Forderungen; Veranlassen zusätzlich '
            + 'erforderlicher Ermittlungen und Messungen; Bewerten der Messergebnisse '
            + 'gemeinsam mit den ausführenden Unternehmen; Abstimmen der zeitlichen Abfolge '
            + 'der Einzelgewerke und Bewerten ihrer Auswirkungen aufeinander.',
    },
    {
        key: 'abgrenzung_sigeko', kapitel_key: 'koordinator', pflicht: true,
        titel: 'Abgrenzung zum SiGeKo',
        regelwerk: 'dguv101_004',
        text: 'Der vom Bauherrn bestellte Koordinator nach DGUV Regel 101-004 übernimmt '
            + 'nicht automatisch die Aufgaben eines Koordinators nach Baustellenverordnung. '
            + 'Dieser ist gesondert zu bestellen; beide Aufgaben können von derselben Person '
            + 'wahrgenommen werden. Die Eigenverantwortung der Auftragnehmer für ihren '
            + 'Zuständigkeitsbereich bleibt unberührt.',
    },

    // ── 1.5 Personenkreis ──────────────────────────────────────────────────
    {
        key: 'alleinarbeit', kapitel_key: 'personenkreis', pflicht: true,
        titel: 'Alleinarbeit',
        text: 'Alleinarbeit im Schwarzbereich ist grundsätzlich unzulässig; es gilt '
            + 'mindestens das Zwei-Personen-Prinzip. Ist sie in begründeten Ausnahmefällen '
            + 'unvermeidbar, ist eine Personen-Notsignal-Anlage nach DGUV Regel 112-139 zu '
            + 'verwenden oder eine ständige Funk- oder Sichtverbindung zu einer dauerhaft '
            + 'besetzten Stelle außerhalb des kontaminierten Bereichs sicherzustellen. Die '
            + 'Aufsicht stellt in regelmäßigen Abständen aktiv Kontakt her und dokumentiert '
            + 'ihn; bleibt die Rückmeldung aus, sind unverzüglich Rettungsmaßnahmen '
            + 'einzuleiten.',
    },

    // ── 1.6 Aufsicht ───────────────────────────────────────────────────────
    {
        // Der Lehrgangsumfang wird NICHT beziffert: die Zahl (verbreitet sind
        // 32 Lehreinheiten) laesst sich hier nicht belegen — die Regel liegt
        // nicht im Repo, und die Bausteine tragen keine Belegmarkierung wie
        // die Tragezeiten in `tragezeit.ts`. Eine ungepruefte Zahl in einem
        // gedruckten Plan ist genau der Fehler, den Regel 4 verbietet
        // („Was nicht ableitbar ist, wird nicht geraten“). Genannt wird die
        // Fundstelle; wer den Nachweis prueft, liest den Umfang dort.
        key: 'sachkunde', kapitel_key: 'aufsicht', pflicht: true,
        titel: 'Fachlich geeigneter Vorgesetzter',
        text: 'Arbeiten in kontaminierten Bereichen müssen von einem fachlich geeigneten '
            + 'Vorgesetzten geleitet werden, der mit den besonderen Gefahren vertraut ist. '
            + 'Ausreichende Kenntnisse werden durch die Teilnahme an einem Sachkundelehrgang '
            + 'nach Anhang 6A der DGUV Regel 101-004 erworben; Umfang und Inhalt des '
            + 'Lehrgangs richten sich nach diesem Anhang. '
            + 'Aufsichtsführender ist, wer die arbeitssichere Durchführung zu überwachen hat; '
            + 'er muss weisungsbefugt sein.',
    },

    // ── 1.7 Gültigkeit ─────────────────────────────────────────────────────
    {
        key: 'ende_geltung', kapitel_key: 'gueltigkeit', pflicht: true,
        titel: 'Ende der Geltung',
        text: 'Die Gültigkeit endet nach Abschluss der genannten Arbeitsschritte '
            + 'einschließlich der Abfuhr belasteter Materialien, nach Reinigung sämtlicher '
            + 'Geräte, Anlagen und Fahrzeuge sowie nach Reinigung und Abnahme aller Flächen. '
            + 'Werden in als unbelastet eingestuften Bereichen Schadstoffe oder '
            + 'Auffälligkeiten angetroffen, sind die Arbeiten einzustellen und der Bereich '
            + 'ist neu zu bewerten.',
    },

    // ── 1.8 Anzeige ────────────────────────────────────────────────────────
    {
        key: 'anzeige_bg', kapitel_key: 'anzeige', pflicht: true,
        titel: 'Anzeige an die Berufsgenossenschaft',
        regelwerk: 'dguv101_004',
        text: 'Der Auftragnehmer hat die Arbeiten in kontaminierten Bereichen spätestens vier '
            + 'Wochen vor Beginn schriftlich bei der für ihn zuständigen Berufsgenossenschaft '
            + 'anzuzeigen. Beizufügen sind die Auflistung der Gefahrstoffe und biologischen '
            + 'Arbeitsstoffe, die Beschreibung der Baumaßnahme und der Arbeitsverfahren, die '
            + 'vorgesehenen Sicherheitsmaßnahmen sowie die tätigkeitsbezogene '
            + 'Betriebsanweisung. Eine Kopie der Anzeige ist dem Koordinator und der '
            + 'Bauleitung vorzulegen.',
    },

    // ── 3.3 Plausibilisierung ──────────────────────────────────────────────
    {
        key: 'worst_case', kapitel_key: 'plausibilitaet', pflicht: true,
        titel: 'Maßgeblich ist der ungünstigste Fall',
        text: 'Nach TRGS 524 Nr. 4.1 Abs. 15 sind bei nicht ausreichenden Kenntnissen über '
            + 'die Gefahren maximale Sicherheitsmaßnahmen anzusetzen. Die Schutzklassen je '
            + 'Arbeitsbereich orientieren sich deshalb nicht am Mittelwert der Proben, '
            + 'sondern an der höchsten Einstufung des jeweiligen Bereichs. Eine durch '
            + 'Mischprobenbildung verursachte Verdünnung eines lokalen Hotspots ändert daran '
            + 'nichts.',
    },
    {
        key: 'laga_grenze', kapitel_key: 'plausibilitaet', pflicht: false,
        titel: 'Grenzen abfallrechtlicher Beurteilungen',
        text: 'Die abfallrechtliche Deklaration verfolgt einen anderen Zweck als der '
            + 'Arbeitsschutz: Mischproben mitteln über Flächen und Schichten, und die '
            + 'Aufnahmewege der Beschäftigten spielen dort keine Rolle. Die Einstufungen '
            + 'dienen hier als Ausgangspunkt; maßgeblich für die Schutzmaßnahmen ist die '
            + 'tätigkeitsbezogene Beurteilung.',
    },

    // ── 6 Wirkungspfade ────────────────────────────────────────────────────
    {
        key: 'freisetzung', kapitel_key: 'wirkungspfade', pflicht: true,
        titel: 'Freisetzung erst durch die Arbeiten',
        text: 'In der ungestörten Bodenmatrix liegen die Stoffe nicht in atembarer Form vor. '
            + 'Erst durch Aushub, Umlagern, Befahren, Abrieb und Trocknung kann es zur '
            + 'Freisetzung kommen. Maßgebliche Aufnahmewege sind das Einatmen von Stäuben '
            + 'und Aerosolen, der Hautkontakt mit belastetem Material und die orale Aufnahme '
            + 'durch Hand-zu-Mund-Kontakt bei unzureichender Hygiene.',
    },

    // ── 7.1 Technisch ──────────────────────────────────────────────────────
    {
        key: 'stop', kapitel_key: 'technisch', pflicht: true,
        titel: 'Rangfolge der Schutzmaßnahmen',
        text: 'Technische und organisatorische Schutzmaßnahmen haben Vorrang. Erst wenn beide '
            + 'keine ausreichende Sicherheit bieten, sind persönliche Schutzmaßnahmen '
            + 'anzuwenden. Vorrangig ist das Arbeitsverfahren mit der geringsten Gefährdung '
            + 'zu wählen: expositionsarme Verfahren, Atemluftversorgung der Baumaschinen, '
            + 'Befeuchtung und staubarme Bearbeitung.',
    },
    {
        key: 'maschinen', kapitel_key: 'technisch', pflicht: true,
        titel: 'Maschinen, Fahrzeuge und Geräte',
        text: 'Es sind Fahrzeuge und Baumaschinen mit Schutzbelüftung (Filter der Klasse H13) '
            + 'einzusetzen; die Fahrerkabinen bleiben im Schwarzbereich geschlossen. Ist eine '
            + 'Schutzbelüftung technisch nicht möglich, tragen die Mitarbeiter die für den '
            + 'Bereich festgelegte Schutzausrüstung. Fahrzeuge, die aus dem Schwarzbereich in '
            + 'den Weißbereich wechseln, sind zu reinigen; anfallendes Wasser ist aufzufangen '
            + 'und fachgerecht zu entsorgen.',
    },

    // ── 7.2 Organisatorisch ────────────────────────────────────────────────
    {
        key: 'zonen', kapitel_key: 'organisatorisch', pflicht: true,
        titel: 'Einteilung in Schutzzonen',
        text: 'Der Übergang vom Schwarz- in den Weißbereich erfolgt ausschließlich über die '
            + 'gekennzeichneten Zugänge. Während stark staubfreisetzender Arbeiten werden in '
            + 'demselben Bereich keine anderen Arbeiten ausgeführt. Bei Staubentwicklung ist '
            + 'der Boden zu befeuchten. Pausen- und Aufenthaltsbereiche liegen außerhalb der '
            + 'belasteten Bereiche.',
    },
    {
        key: 'ausschleusen', kapitel_key: 'organisatorisch', pflicht: true,
        titel: 'Ausschleusen von Material und Geräten',
        text: 'Für den Transport von Abfällen, Materialien und Werkzeugen aus dem '
            + 'Schwarzbereich ist die Staubfreisetzung zu minimieren; sie sind zu verpacken '
            + 'oder vor dem Ausschleusen zu reinigen. Belasteter Boden wird nur feucht oder '
            + 'abgedeckt transportiert, außerhalb des Geländes nur mit geschlossener Plane. '
            + 'Die Zwischenlagerung erfolgt auf festem Untergrund mit Folienabdeckung.',
    },
    {
        key: 'entsorgung', kapitel_key: 'organisatorisch', pflicht: false,
        titel: 'Entsorgung',
        text: 'Sämtliche belasteten Materialien sind ordnungsgemäß zu entsorgen. '
            + 'Reinigungswasser und abgesetzte Schlämme sind zu untersuchen und nach Analytik '
            + 'zu entsorgen. Die ordnungsgemäße Entsorgung ist mit Entsorgungsnachweisen, '
            + 'Wiegescheinen und Begleitscheinen zu dokumentieren.',
    },

    // ── 7.2.3 Hygiene ──────────────────────────────────────────────────────
    {
        key: 'grundregeln', kapitel_key: 'hygiene', pflicht: true,
        titel: 'Allgemeine Verhaltensregeln',
        text: 'Rauchen, Essen und Trinken sind im Schwarzbereich verboten; Gegenstände des '
            + 'persönlichen Gebrauchs bleiben draußen. Bei jeder Arbeitsunterbrechung wird '
            + 'die Einwegschutzkleidung in den bereitgestellten Behältern entsorgt; eine '
            + 'Mehrfachverwendung findet nicht statt. Beim Verlassen des Bereichs werden die '
            + 'Schutzausrüstung abgelegt, Hände und Gesicht gewaschen und das Schuhwerk '
            + 'gereinigt oder gewechselt. Nach jedem Arbeitstag ist zu duschen.',
    },

    // ── 7.2.4 Arbeitsmedizin ───────────────────────────────────────────────
    {
        key: 'vorsorge', kapitel_key: 'arbeitsmedizin', pflicht: true,
        titel: 'Arbeitsmedizinische Vorsorge',
        text: 'Der Auftragnehmer sorgt für eine angemessene arbeitsmedizinische Vorsorge und '
            + 'beauftragt hierfür einen Facharzt für Arbeitsmedizin. Erforderlich sind die '
            + 'Pflichtvorsorge vor Arbeitsbeginn, die Feststellung der Atemschutztauglichkeit '
            + 'für alle Träger von Masken oder Gebläsegeräten sowie die Nachuntersuchung nach '
            + 'Abschluss der Arbeiten. Beschäftigte dürfen nur eingesetzt werden, wenn die '
            + 'Vorsorge fristgerecht durchgeführt wurde; der Nachweis ist auf der Baustelle '
            + 'vorzuhalten.',
    },
    {
        key: 'biomonitoring', kapitel_key: 'arbeitsmedizin', pflicht: false,
        titel: 'Biomonitoring',
        text: 'Ergänzend wird ein Biomonitoring nach TRGS 710 in Verbindung mit den '
            + 'BAT-Werten der TRGS 903 durchgeführt: vor Arbeitsbeginn als Ausgangswert, im '
            + 'Verlauf bei fortdauernder Exposition und nach Arbeitsende. Die Befunde sind '
            + 'vertraulich; dem Arbeitgeber wird ausschließlich die Beurteilung „geeignet / '
            + 'nicht geeignet / Maßnahmen erforderlich" mitgeteilt. Bei auffälligen Werten ist '
            + 'die Belastungssituation am Arbeitsplatz neu zu bewerten.',
    },

    // ── 7.2.6 Notfall ──────────────────────────────────────────────────────
    {
        key: 'unregelmaessigkeiten', kapitel_key: 'notfall', pflicht: true,
        titel: 'Verhalten bei Unregelmäßigkeiten',
        text: 'Werden Unregelmäßigkeiten festgestellt, sind die Arbeiten unverzüglich zu '
            + 'unterbrechen, der Gefahrenbereich ist zu verlassen und der Koordinator zu '
            + 'verständigen. Die Arbeiten dürfen erst fortgesetzt werden, nachdem er dies '
            + 'angeordnet und die einzuhaltenden Schutzmaßnahmen festgelegt hat. Als '
            + 'Unregelmäßigkeiten gelten unvermutet auftretende Gefahrstoffe, Gebinde mit '
            + 'unbekanntem Inhalt, auffälliger Geruch, sichtbare Verfärbungen sowie '
            + 'Auffälligkeiten bei den Messungen.',
    },

    // ── 7.2.9 Beschäftigungsbeschränkungen ─────────────────────────────────
    {
        key: 'beschraenkungen', kapitel_key: 'beschaeftigung', pflicht: true,
        titel: 'Besondere Personengruppen',
        text: 'Für Jugendliche gilt § 22 JArbSchG. Werdende und stillende Mütter sind '
            + 'grundsätzlich nicht einzusetzen. Beim Umgang mit blei- und quecksilberhaltigen '
            + 'Gefahrstoffen besteht für gebärfähige Arbeitnehmerinnen ein '
            + 'Beschäftigungsverbot bei Überschreitung des biologischen Grenzwerts; für Blei '
            + 'gilt für sie der niedrigere Wert. Personen mit bestehenden neurologischen '
            + 'Symptomen, Atemwegs- oder Hauterkrankungen sind gesondert zu beurteilen.',
    },

    // ── 7.2.10 Unterweisung ────────────────────────────────────────────────
    {
        key: 'unterweisung_takt', kapitel_key: 'unterweisung', pflicht: true,
        titel: 'Unterweisung der Beschäftigten',
        text: 'Alle im belasteten Bereich eingesetzten Arbeitskräfte werden vor '
            + 'Arbeitsaufnahme, bei wesentlichen Änderungen der Arbeitsbedingungen und '
            + 'danach in regelmäßigen Abständen unterwiesen. Wesentlicher Bestandteil ist die '
            + 'Schulung zur persönlichen Hygiene. Grundlage ist die tätigkeitsbezogene '
            + 'Betriebsanweisung des Auftragnehmers. Inhalt und Zeitpunkt sind schriftlich '
            + 'festzuhalten und von den Unterwiesenen zu bestätigen; die Dokumentation ist auf '
            + 'der Baustelle vorzuhalten.',
    },

    // ── 7.2.13 Explosionsschutz ────────────────────────────────────────────
    {
        key: 'heissarbeiten', kapitel_key: 'explosionsschutz', pflicht: true,
        titel: 'Heißarbeiten',
        text: 'Heißarbeiten (Schweißen, Brennschneiden, Trennen) sind im Schwarzbereich '
            + 'grundsätzlich nicht zulässig; sie finden im Weißbereich statt. Ist im '
            + 'Ausnahmefall eine Heißarbeit im Übergangsbereich erforderlich, ist vorher eine '
            + 'messtechnische Überwachung mit Mehrfachgaswarngerät durchzuführen. Vor '
            + 'Heißarbeiten erstellt der Auftragnehmer einen Erlaubnisschein und informiert '
            + 'Bauherrn, Bauleitung und Koordinator; darin sind die zusätzlichen '
            + 'Brandschutzmaßnahmen zu benennen.',
    },
    {
        key: 'ex_atmosphaere', kapitel_key: 'explosionsschutz', pflicht: false,
        titel: 'Explosionsfähige Atmosphäre',
        text: 'Bei Arbeiten in belasteten Böden können durch leichtflüchtige Begleitstoffe '
            + 'oder beim Anbohren bislang ungeprüfter Bereiche explosionsfähige Atmosphären '
            + 'nicht generell ausgeschlossen werden. Werden sie festgestellt, sind die '
            + 'Maßnahmen nach Betriebssicherheitsverordnung einzuleiten: Zoneneinteilung, '
            + 'explosionsgeschützte Geräte, Vermeidung von Zündquellen, '
            + 'Explosionsschutzdokument und Lüftung.',
    },

    // ── 10 Erste Hilfe ─────────────────────────────────────────────────────
    {
        key: 'ersthelfer', kapitel_key: 'erste_hilfe', pflicht: true,
        titel: 'Ersthelfer und Ausstattung',
        text: 'Jedes ausführende Unternehmen stellt ausgebildete Ersthelfer vor Ort und meldet '
            + 'sie vor Arbeitsbeginn dem Koordinator. Bei Arbeiten mit Atemschutz unter '
            + 'Belastung ist zusätzlich ein Helfer mit Kenntnissen zur Atemschutz-Notfallhilfe '
            + 'verfügbar. Vorzuhalten sind Sanitätskasten, Not- und Augendusche im Bereich der '
            + 'Schwarz-Weiß-Anlage, ein Mobiltelefon mit eingespeicherten Notrufnummern sowie '
            + 'eine Krankentrage. Bei bekannter oder vermuteter Aufnahme von Gefahrstoffen ist '
            + 'unverzüglich eine ärztliche Untersuchung zu veranlassen.',
    },

    // ── 12 Brandschutz ─────────────────────────────────────────────────────
    {
        key: 'brandverhuetung', kapitel_key: 'brandschutz', pflicht: true,
        titel: 'Brandverhütung und Alarmierung',
        text: 'Im Schwarzbereich gilt Rauch- und Feuerverbot; Zündquellen sind zu vermeiden. '
            + 'Brennbare Stoffe werden nur in zugelassenen Behältern auf der '
            + 'Baustelleneinrichtungsfläche gelagert. Im Brandfall sind Feuerwehr und '
            + 'Bauleitung zu alarmieren, Personen zu retten, der Sammelplatz aufzusuchen und '
            + 'eine Anwesenheitskontrolle durchzuführen. Die Feuerwehr ist einzuweisen und auf '
            + 'die Gefahrstoffe hinzuweisen, wegen der Gefahr toxischer Brandgase.',
    },

    // ── 13 Hautschutz ──────────────────────────────────────────────────────
    {
        key: 'hautschutzplan', kapitel_key: 'hautschutz', pflicht: true,
        titel: 'Hautschutz, Hautreinigung, Hautpflege',
        text: 'Der Auftragnehmer stellt geeignete Hautschutz-, Hautreinigungs- und '
            + 'Hautpflegemittel zur Verfügung. Hautschutzmittel werden vor Arbeitsbeginn und '
            + 'nach Pausen aufgetragen, die Hautreinigung erfolgt vor Pausen und beim '
            + 'Verlassen des Schwarzbereichs, die Hautpflege nach jeder Reinigung. Der '
            + 'Hautschutzplan ist im Schwarz-Weiß-Container auszuhängen. Bei Hautreaktionen '
            + 'sind der Betriebsarzt zu konsultieren und der Koordinator zu informieren.',
    },
];

const NACH_KAPITEL = new Map<string, Baustein[]>();
for (const b of BAUSTEINE) {
    const liste = NACH_KAPITEL.get(b.kapitel_key) ?? [];
    liste.push(b);
    NACH_KAPITEL.set(b.kapitel_key, liste);
}

export const bausteineDesKapitels = (kapitel_key: string): Baustein[] =>
    NACH_KAPITEL.get(kapitel_key) ?? [];

export const baustein = (kapitel_key: string, key: string): Baustein | null =>
    bausteineDesKapitels(kapitel_key).find(b => b.key === key) ?? null;

/**
 * Der Wortlaut, der im Plan steht.
 *
 * Ein gespeicherter Wortlaut geht vor: er friert gegen Katalogaenderungen ein,
 * und genau dafuer ist er da.
 */
export const wortlaut = (b: Baustein, override?: string): string =>
    (override && override.trim()) ? override : b.text;
